import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

export const useAssetDetailTour = () => {
    const { t } = useTranslation('asset')
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const TOUR_STEPS: TourStep[] = useMemo(
        () => [
            {
                selector: '[data-tour="asset-header-info"]',
                title: t('tour.assetDetail.info.title', 'Thông tin chung'),
                description: t(
                    'tour.assetDetail.info.description',
                    'Xem thông tin cơ bản của tài liệu bao gồm tên, mã số, người tạo và phòng ban.'
                ),
            },
            {
                selector: '[data-tour="asset-copy-link"]',
                title: t('tour.assetDetail.copyLink.title', 'Chia sẻ liên kết'),
                description: t(
                    'tour.assetDetail.copyLink.description',
                    'Sao chép đường dẫn tài liệu để chia sẻ nhanh chóng với người khác.'
                ),
            },
            {
                selector: '[data-tour="asset-detail-tabs"]',
                title: t('tour.assetDetail.tabs.title', 'Chi tiết tài liệu'),
                description: t(
                    'tour.assetDetail.tabs.description',
                    'Các tab thông tin chi tiết:\n• Tổng quan: Xem mô tả và thông tin chi tiết\n• Tệp đính kèm: Xem các tệp đính kèm của tài liệu\n• Phê duyệt: Xem lịch sử và trạng thái phê duyệt\n• Chia sẻ: Xem quyền truy cập của tài liệu'
                ),
            },
        ],
        [t]
    )

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [TOUR_STEPS])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStepRef = useRef<
        (stepIndex: number, steps: TourStep[]) => void
    >(() => {})

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStepRef.current(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () =>
                                  showTourStepRef.current(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => showTourStepRef.current(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    useEffect(() => {
        showTourStepRef.current = showTourStep
    }, [showTourStep])

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible tour elements found')
            return
        }

        setIsActive(true)
        setCurrentStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourStep(0, visibleSteps)
        }, 300)
    }, [getVisibleSteps, showTourStep])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}
